import React, { Component, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route } from 'react-router-dom';
import './App.scss';
import { composePS } from '@cd/pipe';
import ErrorBoundary from './ErrorBoundary';
import { ThemeProvider } from '@mui/material/styles';
import Wrapper from './Wrapper';
import { Switch, useHistory } from 'react-router';
import Login from './login/components/Login';
import Register from './login/components/Register';
import Logout from './login/components/Logout';
import { either } from './lib/utils/utils';
import { postJSONPS } from './lib/utils/fetch';
import AlertDlgContext from './AlertDlgContext';
import AlertDialog from './login/components/AlertDialog';
import ForgotPassword from './login/components/ForgotPassword';
import CssBaseline from '@mui/material/CssBaseline';
import RecoverPassword from './login/components/RecoverPassword';
import { DynamicImport, DynamicDefaultCmp } from './DynamicImport';
import LandingHeader from './login/components/LandingHeader';
import LandingFooter from './login/components/LandingFooter';
import AdminLogin from './admin/AdminLogin';
import AdminDashboard from './admin/AdminDashboard';
import ImpersonationBanner from './admin/ImpersonationBanner';
import { ADMIN_PATH, ADMIN_LOGIN_PATH } from './admin/adminPath';
import theme from './theme';
import { ThemeProviderCD } from '@cd/rhf';

const debug = false;
/** postSessionValid ::  Promise(Either) */
const postSessionValid = () =>
	composePS(postJSONPS)('POST', `/sessionValid`, {
		body: JSON.stringify({})
	});

/* const theme = createTheme({
	palette: {
		mode: 'dark',
		primary: { main: blue[700] }, // Purple and green play nicely together.
		secondary: { main: orange[500] } // This is just green.A700 as hex.
	},
	typography: { useNextVariants: true },
	breakpoints: {
		values: {
			xs: 0,
			sm: 500,
			md: 960,
			lg: 1280,
			xl: 1920
		}
	}
}); */

const changeAuthRoute = (isAuth, securityQuestions, history) =>
	debug
		? 1
		: history.push(
				isAuth
					? securityQuestions
						? '/dashboard/view'
						: '/security/f'
					: '/login'
		  );

const LoginCtrl = () => {
	const history = useHistory();
	// eslint-disable-next-line no-unused-vars
	const [isAuth, setIsAuth] = useState(false);
	const [securityQuestions, setSecurityQuestions] = useState(undefined);
	const [alertDlg, setAlertDlg] = useState({ alertDlgOpen: 0 });

	// Remaps + merges the incoming dlg payload, matching the old
	// updateAlertDlgOpen setter (missing keys become undefined).
	const updateAlertDlgOpen = ({
		alertDlgOpen,
		message,
		title,
		buttonText,
		okCallback,
		fullScreen
	}) =>
		setAlertDlg(prev => ({
			...prev,
			alertDlgOkCallback: okCallback,
			alertDlgOpen,
			alertDlgMsg: message,
			alertDlgButtonText: buttonText,
			alertDlgTitle: title,
			alertDlgFullScreen: fullScreen
		}));

	const updateIsAuth = nextIsAuth => {
		setTimeout(() => changeAuthRoute(nextIsAuth, undefined, history), 50);
		setIsAuth(nextIsAuth);
	};

	useEffect(() => {
		// The admin console (ADMIN_PATH, ADMIN_LOGIN_PATH) runs its own auth
		// flow (allow-list + 2FA, and AdminDashboard's own session check).
		// Skip the member auth redirect so it doesn't bounce admins to
		// /login or the member dashboard before the console can render.
		if (history.location.pathname.startsWith(ADMIN_PATH)) {
			return;
		}

		postSessionValid().then(
			either(console.log, ({ isAuth, securityQuestions }) => {
				changeAuthRoute(isAuth, securityQuestions, history);
				setSecurityQuestions(securityQuestions);
			})
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const onLogin = v => {
		if (v === 'OK' || v === 'OKS') {
			changeAuthRoute(true, v === 'OK', history);
		}
	};

	const onLogout = () => {
		updateIsAuth(false);
	};

	const onOpenAlertDlg = ({
		title,
		buttonText,
		message,
		okCallback,
		fullScreen = false
	}) => {
		updateAlertDlgOpen({
			alertDlgOpen: true,
			buttonText,
			message,
			title,
			okCallback,
			fullScreen
		});
	};

	const onCloseAlertDlg = () => {
		const cb = alertDlg.alertDlgOkCallback;
		updateAlertDlgOpen({
			alertDlgOpen: 0,
			alertDlgOkCallback: null,
			buttonText: null
		});
		if (cb) {
			cb();
		}
	};

	const onCancelAlertDlg = () => {
		const cb = alertDlg.alertDlgCancelCallback;
		updateAlertDlgOpen({
			alertDlgOpen: 0,
			alertDlgOkCallback: null,
			buttonText: null
		});
		if (cb) {
			cb();
		}
	};

	const {
		alertDlgOpen,
		alertDlgMsg,
		alertDlgButtonText,
		alertDlgTitle,
		alertDlgFullScreen
	} = alertDlg;

	return (
		<ErrorBoundary>
			<ThemeProvider theme={theme}>
				<ThemeProviderCD theme={theme}>
					<CssBaseline />

					<AlertDlgContext.Provider
						value={{
							securityQuestions,
							onOpenAlertDlg,
							onCloseAlertDlg,
							alertDlgOpen
						}}>
						<div className="App">
							<ImpersonationBanner />
							<Route
								path={[
									'/login',
									'/register',
									'/forgotPassword',
									'/recoverpassword'
								]}
								component={LandingHeader}
							/>
							<article className="App-content">
								<Switch>
									<Route
										path="/login"
										render={() => (
											<Login onLogin={onLogin} />
										)}
									/>
									<Route
										path="/register"
										render={() => <Register />}
									/>
									<Route
										path="/forgotPassword"
										render={() => <ForgotPassword />}
									/>
									<Route
										path="/recoverPassword"
										render={() => <RecoverPassword />}
									/>
									<Route
										path="/signout/quit"
										render={() => (
											<Logout
												onLogout={onLogout}
												title="Logging out?"
												message="Are you sure?"
											/>
										)}
									/>
									<Route
										path="/security/f"
										render={props => (
											<DynamicImport
												load={() =>
													import(
														`dynamicForms/components/Security.js`
													)
												}
												{...props}
												editing={true}
												hideCancel={true}>
												{DynamicDefaultCmp}
											</DynamicImport>
										)}
									/>
									<Route
										path={ADMIN_LOGIN_PATH}
										render={() => <AdminLogin />}
									/>
									<Route
										path={ADMIN_PATH}
										render={() => <AdminDashboard />}
									/>
									<Route
										path="/"
										render={props => <Wrapper {...props} />}
									/>
								</Switch>
							</article>
							<Route
								path={[
									'/login',
									'/register',
									'/forgotPassword',
									'/recoverpassword'
								]}
								component={LandingFooter}
							/>
							<AlertDialog
								fullScreen={alertDlgFullScreen}
								openDlg={alertDlgOpen}
								title={alertDlgTitle}
								message={alertDlgMsg}
								buttonText={alertDlgButtonText || ['OK']}
								onOK={onCloseAlertDlg}
								onCancel={onCancelAlertDlg}
							/>
						</div>
					</AlertDlgContext.Provider>
				</ThemeProviderCD>
			</ThemeProvider>
		</ErrorBoundary>
	);
};

class App extends Component {
	render() {
		return (
			<Router>
				<LoginCtrl />
			</Router>
		);
	}
}

export default App;
